import React, {useEffect, useState} from 'react';

import {createSelector} from '@reduxjs/toolkit';
import {useSelector} from 'react-redux';
import styled from 'styled-components';

import {EnabledDesktopNotificationsBanner} from 'app/components/general/EnableDesktopNotificationsBanner';
import {WarnEmailNotVerifiedBanner} from 'app/components/general/WarnEmailNotVerifiedBanner';
import {UpgradeModalContainer} from 'app/containers/general/UpgradeModalContainer';
import {
  CurrentUserSelector,
  subscribeToCurrentUser,
} from 'app/current-user/api';
import {KeyboardShortcutsHelpModal} from 'app/general/shortcuts/KeyboardShortcutsHelpModal';
import {KeyboardShortcutsProvider} from 'app/general/shortcuts/KeyboardShortcutsProvider';
import {ErrorBoundary} from 'app/general/structures/ErrorBoundary';
import {FlexPanel} from 'app/general/structures/FlexPanel';
import {ConnectedSweetLoadingBar} from 'app/general/sweet-loading-bar';
import {receiveNotificationsStats} from 'app/notifly/actions';
import {PageHeadNotificationsSyncer} from 'app/notifly/components/PageHeadNotificationsSyncer';
import {presenceWebsocketRouter} from 'app/presence/presence-ws-router';
import {
  accountSettingsSelector,
  subscribeToAccountSettings,
} from 'app/settings/api';
import {ReloadRequiredModal} from 'app/settings/components/ReloadRequiredModal';
import {loadStatefromLocalStorage} from 'app/store/local-storage';
import {useModelChangeSubscription} from 'app/subscriptions';
import {ToastNotificationsContainer} from 'app/toasts/components/ToastNotificationsContainer';
import {DEFAULT_LANG} from 'app/utils/constants';
import {setLocale} from 'app/utils/i18n';
import {hideLoadingAnimation} from 'app/utils/loading-animation';
import {useAsyncActions} from 'app/utils/redux';
import {configureSentryScope, setSentryInstalled} from 'app/utils/sentry';
import {setupServiceWorker} from 'app/utils/serviceworkers';
import {WebsocketProvider} from 'app/ws/components/WebsocketProvider';

type Props = {
  children: React.ReactNode;
  disable_ws?: boolean;
};

export const SPBootstrapPage = ({children, disable_ws}: Props): JSX.Element => {
  const appData = useSelector(bootstrapDataSelector);

  const actions = useAsyncActions({
    receiveNotificationsStats,
    subscribeToAccountSettings,
    subscribeToCurrentUser,
    loadStatefromLocalStorage,
  });

  const [readyToMount, setReadyToMount] = useState(false);
  const [wsRegistration, setWsRegistration] = useState<
    ServiceWorkerRegistration | undefined
  >();

  useModelChangeSubscription(
    (message, {runWhenAnyModelMatch, runWhenModelIdMatch}) => {
      // here goes the model-change handler which are not component related
      // and will listen for changes on entire app lifespan
      runWhenAnyModelMatch(
        message,
        'notification',
        actions.receiveNotificationsStats
      );
      if (appData.user_id != null) {
        runWhenModelIdMatch(message, 'user', appData.user_id, () => {
          actions.subscribeToCurrentUser();
          actions.subscribeToAccountSettings();
        });
        runWhenModelIdMatch(message, 'users', appData.user_id, () => {
          actions.subscribeToCurrentUser();
          actions.subscribeToAccountSettings();
        });
      }
    },
    [appData.user_id]
  );

  useEffect(() => {
    actions.subscribeToAccountSettings();
    actions.subscribeToCurrentUser();
  }, []);

  useEffect(() => {
    if (appData.ready && !readyToMount) {
      //eslint-disable-next-line no-underscore-dangle
      window.__getSweetProcessBundlesPublicPath__ = () =>
        //eslint-disable-next-line no-undef
        `${appData.STATIC_URL}${BUNDLES_PATH}`;

      try {
        configureSentryScope((scope) => {
          scope.setUser({
            id: appData.user_id,
          });
          scope.setTag('account', appData.account_email);
        });
        setSentryInstalled(true);
      } catch (error) {
        setSentryInstalled(false);
      }

      actions.loadStatefromLocalStorage(appData.user_id, appData.account_id);

      setLocale(appData.language).then((data: unknown) => {
        setReadyToMount(true);
        hideLoadingAnimation();
        return data; // return data here to keep bluebird happy
      });

      if (!disable_ws) {
        setupServiceWorker().then(
          (wsRegistration) => setWsRegistration(wsRegistration),
          // do nothing if browser do not support service-worker
          () => null
        );
      }
    }
  }, [appData.user_id, appData.account_id]);

  const content = (
    <KeyboardShortcutsProvider>
      <FlexPanel
        type="vertical"
        secondaryZindex="2"
        mainZindex="1"
        secondary={
          <StyledNotificationsDiv>
            <ErrorBoundary name="notifications-banner">
              {wsRegistration != null &&
              appData.vapid_application_server_key != null ? (
                <EnabledDesktopNotificationsBanner
                  wsRegistration={wsRegistration}
                  applicationServerKey={appData.vapid_application_server_key}
                />
              ) : null}
              {appData.is_email_verified === false && appData.email != null ? (
                <WarnEmailNotVerifiedBanner email={appData.email} />
              ) : null}
              <ConnectedSweetLoadingBar />
            </ErrorBoundary>
          </StyledNotificationsDiv>
        }
      >
        {readyToMount ? children : null}
      </FlexPanel>
      <ErrorBoundary name="modals">
        {readyToMount ? <UpgradeModalContainer /> : null}
        <ReloadRequiredModal />
        <PageHeadNotificationsSyncer />
        <ToastNotificationsContainer />
        <KeyboardShortcutsHelpModal />
      </ErrorBoundary>
    </KeyboardShortcutsProvider>
  );

  if (disable_ws === true) {
    return content;
  } else {
    return (
      <WebsocketProvider
        routers={{
          presence: presenceWebsocketRouter,
        }}
      >
        {content}
      </WebsocketProvider>
    );
  }
};

const StyledNotificationsDiv = styled.div`
  > div:not(:first-child) {
    border-top: 1px solid rgba(0, 0, 0, 0.2);
  }
`;

const bootstrapDataSelector = createSelector<
  [typeof CurrentUserSelector, typeof accountSettingsSelector],
  | {
      ready: false;
      user_id: null;
      account_id: null;
      email: string | null;
      account_email: string | null;
      STATIC_URL: string | null;
      language: string | null;
      vapid_application_server_key: string | null;
      is_email_verified: boolean | null;
    }
  | {
      ready: true;
      user_id: number;
      account_id: number;
      email: string;
      account_email: string;
      STATIC_URL: string;
      language: string;
      vapid_application_server_key: string;
      is_email_verified: boolean;
    }
>([CurrentUserSelector, accountSettingsSelector], (userData, settings) => {
  if (userData.isSuccess && settings.isSuccess) {
    return {
      ready: true,
      user_id: userData.data.id,
      account_id: settings.data.account_id,
      email: userData.data.email,
      language: userData.data.language || DEFAULT_LANG,
      is_email_verified: userData.data.is_email_verified,
      account_email: settings.data.account_email,
      STATIC_URL: settings.data.STATIC_URL,
      vapid_application_server_key: settings.data.vapid_application_server_key,
    };
  }

  if (
    !userData.isUninitialized &&
    userData.data != null &&
    !settings.isUninitialized &&
    settings.data != null
  ) {
    // Data is initiated but a refetch is running - display some stale data until it
    // gets updated. user_id and account_id is set to null for reverse-compatibility
    return {
      ready: false,
      user_id: null,
      account_id: null,
      email: userData.data.email,
      language: userData.data.language || DEFAULT_LANG,
      is_email_verified: userData.data.is_email_verified,
      account_email: settings.data.account_email,
      STATIC_URL: settings.data.STATIC_URL,
      vapid_application_server_key: settings.data.vapid_application_server_key,
    };
  }

  return {
    ready: false,
    user_id: null,
    account_id: null,
    email: null,
    language: null,
    is_email_verified: null,
    account_email: null,
    STATIC_URL: null,
    vapid_application_server_key: null,
  };
});

type NoWSProps = {
  children: React.ReactNode;
};

export function NoWSSPBootstrapPage(props: NoWSProps): JSX.Element {
  return <SPBootstrapPage disable_ws={true}>{props.children}</SPBootstrapPage>;
}
